import { Space, Typography, Flex, Spin, Tooltip } from 'antd';
import { CSSProperties, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import OnDemandRouteAvatar from './OnDemandRouteAvatar';
import { useRoutingDataQuery } from '../../RouteManagement/routingQueries';
import { RoutingType, Route } from '../../RouteManagement/routingApi';
import CreateEventFromRouteModal from './CreateEventFromRouteModal';
import { ScheduleCreateEventFilters, filterRoutes, formatDate, formatRelativeTime } from './scheduleCreateEventModel';
import { Dayjs } from 'dayjs';

const { Text } = Typography;

const routeRowStyle: CSSProperties = {
  borderBottom: '1px solid #f0f0f0',
  padding: '12px',
  color: '#1e1e1e',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  borderRadius: '4px',
  marginBottom: '4px'
}

type DraggableRouteRowProps = {
  route: Route;
  isDisabled: boolean;
  onClick: () => void;
};

const DraggableRouteRow = ({ route, isDisabled, onClick }: DraggableRouteRowProps) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `route-${route.id}`,
    data: { type: 'route', route },
    disabled: isDisabled,
  });

  const style: CSSProperties = {
    ...routeRowStyle,
    cursor: isDisabled ? 'not-allowed' : 'grab',
    opacity: isDragging ? 0.5 : isDisabled ? 0.5 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      className={isDisabled ? "route-row-disabled" : "route-row-hover"}
      style={style}
      onClick={() => !isDisabled && !isDragging && onClick()}
      {...listeners}
      {...attributes}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Flex justify="space-between" align="center">
          <Text strong style={{ fontWeight: 600 }}>{route.name}</Text>
          <OnDemandRouteAvatar crewMemberIds={route.crewMemberIds || ''} crewLeadId={route.crewLeadId} />
        </Flex>
        <Flex gap={12} align="center">
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Jobs {route.jobsCount || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>|</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Created {formatDate(route.createdAtUtc)}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>|</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Last Edited {formatRelativeTime(route.updatedAtUtc)}
          </Text>
        </Flex>
      </Space>
    </div>
  );
};

type Props = {
  filters: ScheduleCreateEventFilters;
  onEventCreated?: () => void;
  selectedDate?: Dayjs;
};

const OnDemandRoutesList = ({ filters, onEventCreated, selectedDate }: Props) => {
  const { data, isLoading } = useRoutingDataQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);

  const onDemandRoutes = data?.filter(route => route.routeType === RoutingType.OnDemand) || [];
  const filteredRoutes = filterRoutes(onDemandRoutes, filters);

  const handleRouteClick = (route: Route) => {
    setSelectedRoute(route);
    setIsModalOpen(true);
  };

  const handleSaveEvent = () => {
    setIsModalOpen(false);
    setSelectedRoute(null);
    if (onEventCreated) {
      onEventCreated();
    }
  };

  if (isLoading) {
    return <Spin />;
  }

  if (filteredRoutes.length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        No routes available
      </div>
    );
  }

  return (
    <>
      <div>
        {filteredRoutes.map((route) => {
          const isDisabled = !route.jobsCount || route.jobsCount === 0;

          if (isDisabled) {
            return (
              <Tooltip key={route.id} title="This route has no jobs" placement="top">
                <div>
                  <DraggableRouteRow
                    route={route}
                    isDisabled={isDisabled}
                    onClick={() => handleRouteClick(route)}
                  />
                </div>
              </Tooltip>
            );
          }

          return (
            <DraggableRouteRow
              key={route.id}
              route={route}
              isDisabled={isDisabled}
              onClick={() => handleRouteClick(route)}
            />
          );
        })}
      </div>

      <CreateEventFromRouteModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRoute(null);
        }}
        route={selectedRoute}
        onSave={handleSaveEvent}
        selectedDate={selectedDate}
      />

      <style>
        {`
          .route-row-hover:hover {
            background-color: #FFCABD;
          }
          .route-row-disabled:hover {
            background-color: transparent;
          }
        `}
      </style>
    </>
  );
};

export default OnDemandRoutesList;